// src/pages/teacher/TeacherLogin.tsx
// מסך כניסה לאפליקציית גננת — ללא קוד גן

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { authApi } from '../../services/api';
import { LogIn, Eye, EyeOff, GraduationCap, Loader2 } from 'lucide-react';
import FormLabel from '../../components/common/FormLabel';
import { teacherLoginHelpTexts } from '../../constants/helpTexts';

function parseJwtClaims(token: string): Record<string, any> {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return {};
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  } catch {
    return {};
  }
}

import { teacherColors } from '../../styles/colors';

const colors = {
  ...teacherColors,
  background: '#faf5ff',
  error: '#ef4444',
};

export default function TeacherLogin() {
  const navigate = useNavigate();
  const { user, token, setUser, setToken, setRefreshToken, setTokenExpiresAt, setRequiresAgreement } = useAuthStore();

  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    document.title = 'KipiKid - גננת';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', colors.primary);

    // If user already has a token (from Unified Login), fetch their KGs
    if (token && user) {
      checkExistingAuth();
    } else {
      setCheckingAuth(false);
    }
  }, []);

  const checkExistingAuth = async () => {
    try {
      // If already has KG context, go to dashboard
      if (user?.kindergartenPublicId) {
        navigate('/teacher/dashboard', { replace: true });
        return;
      }
      // Fetch KGs for this user
      const res = await authApi.getTeacherKindergartens();
      if (res.success && res.data && res.data.length > 0) {
        if (res.data.length === 1) {
          // Auto-select single KG
          const kgRes = await authApi.teacherSelectKg(res.data[0].publicId);
          if (kgRes.success && kgRes.data) {
            const data = kgRes.data;
            setToken(data.accessToken);
            if (data.refreshToken) setRefreshToken(data.refreshToken);
            if (data.expiresAt) setTokenExpiresAt(data.expiresAt);
            setUser({
              ...user!,
              role: 'teacher',
              kindergartenPublicId: data.user?.kindergartenPublicId || res.data[0].publicId,
              kindergartenName: data.user?.kindergartenName || res.data[0].name,
            });
            navigate('/teacher/dashboard', { replace: true });
            return;
          }
        } else {
          // Multiple KGs — go to selection
          sessionStorage.setItem('teacher-kindergartens', JSON.stringify(res.data));
          navigate('/teacher/select-kindergarten', { replace: true });
          return;
        }
      }
    } catch (err) {
      console.error('Auto-auth check failed:', err);
    }
    setCheckingAuth(false);
  };

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!username.trim() || !password) {
        setError('יש להזין שם משתמש וסיסמה');
        setIsLoading(false);
        return;
      }

      const result = await authApi.teacherLogin(username.trim(), password);

      if (result.success && result.data) {
        const data = result.data;
        const claims = parseJwtClaims(data.accessToken);

        setToken(data.accessToken);
        setRefreshToken(data.refreshToken);
        setTokenExpiresAt(data.expiresAt);

        // Check if agreement is required
        if (data.requiresAgreement) {
          setRequiresAgreement(true, data.agreementVersion);
          navigate('/agreement-approval', { replace: true });
          return;
        }

        const kindergartens = data.kindergartens || [];

        if (kindergartens.length <= 1) {
          // Auto-selected single KG, or management user
          setUser({
            id: data.user?.id?.toString() || claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] || '',
            email: '',
            name: `${data.user?.firstName || ''} ${data.user?.lastName || ''}`.trim(),
            firstName: data.user?.firstName,
            lastName: data.user?.lastName,
            phone: data.user?.phone,
            role: 'staff',
            kindergartenPublicId: data.user?.kindergartenPublicId || kindergartens[0]?.publicId,
            kindergartenName: data.user?.kindergartenName || kindergartens[0]?.name,
            publicId: data.user?.publicId,
            serverRole: data.user?.role,
            organizationPublicId: data.user?.organizationPublicId,
            organizationName: data.user?.organizationName,
          });
          navigate('/teacher/dashboard');
        } else {
          // Multiple KGs — need to select
          // Store temp auth and navigate to selection
          setUser({
            id: data.user?.id?.toString() || '',
            email: '',
            name: `${data.user?.firstName || ''} ${data.user?.lastName || ''}`.trim(),
            firstName: data.user?.firstName,
            lastName: data.user?.lastName,
            phone: data.user?.phone,
            role: 'staff',
            publicId: data.user?.publicId,
            serverRole: data.user?.role,
          });
          // Store kindergartens list in sessionStorage for the selection page
          sessionStorage.setItem('teacher-kindergartens', JSON.stringify(kindergartens));
          navigate('/teacher/select-kindergarten');
        }
      } else {
        setError(result.message || 'שם משתמש או סיסמה שגויים');
      }
    } catch (err: any) {
      console.error('Teacher login error:', err);
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('שגיאת תקשורת — נסי שוב');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div dir="rtl" style={{ minHeight: '100vh', backgroundColor: colors.background, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '60px', height: '60px', borderRadius: '16px', background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)' }}>
            <GraduationCap size={32} color="white" />
          </div>
          <p style={{ fontSize: '14px', color: colors.textLight }}>מתחברת...</p>
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" style={{
      minHeight: '100vh',
      backgroundColor: colors.background,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      {/* Logo */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '32px',
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '20px',
          background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '16px',
          boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
        }}>
          <GraduationCap size={40} color="white" />
        </div>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: colors.primary, margin: 0 }}>
          KipiKid
        </h1>
        <p style={{ fontSize: '16px', color: colors.textLight, marginTop: '4px' }}>
          אפליקציית גננת
        </p>
      </div>

      {/* Login Form */}
      <form onSubmit={handleLogin} style={{
        width: '100%',
        maxWidth: '380px',
        backgroundColor: colors.white,
        borderRadius: '16px',
        padding: '32px 24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: colors.text,
          marginBottom: '24px',
          textAlign: 'center',
        }}>
          כניסה
        </h2>

        {/* Username */}
        <div style={{ marginBottom: '16px' }}>
          <FormLabel text="שם משתמש" helpText={teacherLoginHelpTexts.username.tooltip} />
          <input
            type="text"
            value={username}
            onChange={e => setUsername(e.target.value)}
            placeholder="הכניסי שם משתמש"
            autoComplete="username"
            style={{
              width: '100%',
              padding: '12px 14px',
              border: `1px solid ${colors.border}`,
              borderRadius: '10px',
              fontSize: '16px',
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s',
            }}
            onFocus={e => e.target.style.borderColor = colors.primary}
            onBlur={e => e.target.style.borderColor = colors.border}
          />
        </div>

        {/* Password */}
        <div style={{ marginBottom: '20px' }}>
          <FormLabel text="סיסמה" helpText={teacherLoginHelpTexts.password.tooltip} />
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="הכניסי סיסמה"
              autoComplete="current-password"
              style={{
                width: '100%',
                padding: '12px 44px 12px 14px',
                border: `1px solid ${colors.border}`,
                borderRadius: '10px',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = colors.primary}
              onBlur={e => e.target.style.borderColor = colors.border}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
              }}
            >
              {showPassword ? <EyeOff size={20} color={colors.textLight} /> : <Eye size={20} color={colors.textLight} />}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: '10px 14px',
            backgroundColor: '#fee2e2',
            borderRadius: '8px',
            marginBottom: '16px',
          }}>
            <p style={{ fontSize: '14px', color: colors.error, margin: 0 }}>{error}</p>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '14px',
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'opacity 0.2s',
          }}
        >
          {isLoading ? (
            <span>מתחברת...</span>
          ) : (
            <>
              <LogIn size={20} />
              <span>כניסה</span>
            </>
          )}
        </button>
      </form>

      {/* Footer */}
      <p style={{
        marginTop: '24px',
        fontSize: '12px',
        color: colors.textLight,
      }}>
        KipiKid &copy; 2026
      </p>
    </div>
  );
}
